/* GPX export — a route out of the dash and onto the Axiom without going
 * through ops/gpx-sync.py's whole-database round trip (docs/PLOTTER-DATA.md).
 * That script owns the identity-preserving sync (Raymarine GUIDs, the
 * server's full waypoint/route set); this is the other case — "I just drew
 * or autorouted ONE thing on the chart, get it onto the plotter" — pure and
 * client-side, no SignalK write, no ssh.
 *
 * Same shape gpx-sync.py's write_gpx() emits: every route point mirrored as
 * a standalone `<wpt>` as well as an `<rtept>`, because that is the
 * PLOTTER'S own model, not just a SignalK convenience — LightHouse stores a
 * route as references into its waypoint database (PLOTTER-DATA.md: "a route
 * on the plotter is a list of references into the waypoint database"), and
 * an import that only wrote the `<rte>` would leave the route pointing at
 * waypoints that were never created.
 *
 * NO `raymarine:` extension, matching that script's own `--no-guid` case: a
 * route drawn in the dash has no plotter GUID to preserve, and inventing
 * one here would risk colliding with an identity gpx-sync.py is careful to
 * keep inside its own `da5b-0001` namespace — that decision belongs to the
 * server-side sync, which is the only thing that also reads GUIDs back in.
 */

const GPX_NS = 'http://www.topografix.com/GPX/1/1';

function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export interface GpxPoint {
  lat: number;
  lon: number;
  /** Falls back to "`routeName` `N`" when absent — the same convention
   *  ops/gpx-sync.py's `sk_to_rte()` uses, so a route named on the plotter
   *  once reads the same way through either tool. */
  name?: string;
}

function pointXml(tag: 'wpt' | 'rtept', p: GpxPoint, name: string, indent: string): string {
  return [
    `${indent}<${tag} lat="${p.lat.toFixed(7)}" lon="${p.lon.toFixed(7)}">`,
    `${indent} <name>${xmlEscape(name)}</name>`,
    `${indent} <sym>Filled Circle</sym>`,
    `${indent}</${tag}>`
  ].join('\n');
}

/**
 * A single route as GPX 1.1 — the Axiom's LightHouse reads this via
 * My Data > Import/Export > Import from card. Coordinates to 7 decimal
 * places, matching `ops/gpx-sync.py`'s own `COORD_DP` (about 1 cm, far
 * below any plotter's precision, so the two tools' output is directly
 * comparable).
 */
export function routeToGpx(routeName: string, points: readonly GpxPoint[]): string {
  const name = routeName.trim() || 'Route';
  const named = points.map((p, i) => ({ p, name: p.name?.trim() || `${name} ${i + 1}` }));
  const lines = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<gpx xmlns="${GPX_NS}" version="1.1" creator="gennetay-dash">`,
    ...named.map(({ p, name: n }) => pointXml('wpt', p, n, ' ')),
    ' <rte>',
    `  <name>${xmlEscape(name)}</name>`,
    ...named.map(({ p, name: n }) => pointXml('rtept', p, n, '  ')),
    ' </rte>',
    '</gpx>'
  ];
  return lines.join('\n') + '\n';
}

/** A filesystem-safe filename from a route name — spaces to underscores,
 *  anything outside `[A-Za-z0-9_-]` dropped, runs of underscores left by
 *  the drop collapsed to one, so it survives an SD card's own filename
 *  rules without the user having to think about it. */
export function gpxFilename(routeName: string): string {
  const safe = (routeName.trim() || 'route')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .replace(/_+/g, '_');
  return `${safe || 'route'}.gpx`;
}


/**
 * Offer a route as a GPX download.
 *
 * ═══ WHY THIS IS THE ONLY WAY A HOSTED CHART SAVES A ROUTE ═══
 *
 * The boat writes routes back to SignalK, and its plotter picks them up from
 * there. A chart on the web has no server to write to and no business inventing
 * an account system to get one — so the route leaves the way a plotter already
 * accepts one: a file. On a Raymarine that is My Data > Import/Export > Import
 * from card; every other make has the same door.
 *
 * READ-ONLY, so it works on an unsaved draft. There is nothing to fail and
 * nothing to be out of step with.
 */
export function downloadRouteGpx(
  routeName: string,
  points: readonly GpxPoint[]
): void {
  const name = routeName || 'Route';
  const url = URL.createObjectURL(
    new Blob([routeToGpx(name, points)], { type: 'application/gpx+xml' })
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = gpxFilename(name);
  a.click();
  /* Revoked immediately: the click has already handed the blob to the
   * download, and leaving it alive holds the whole file in memory for the life
   * of the page. */
  URL.revokeObjectURL(url);
}
